import { useState, useEffect, useCallback } from 'react';
import { Shield, Play, Plus, Trash2, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { API_BASE, getAuthHeaders, fetchApi } from '../../services/apiClient';

interface GuardrailsStatus {
  enable_guardrails: boolean;
  strict_mode: boolean;
  components: {
    pii_masking: boolean;
    threat_detection: boolean;
    topic_guard: boolean;
    output_guard: boolean;
  };
}

interface TestDataItem {
  label: string;
  text: string;
  expected: string;
  check_type?: string;
  confidence?: number;
}

interface TestResult {
  category: string;
  label: string;
  text: string;
  expected: string;
  actual_safe: boolean;
  actual_safety_level: string;
  blocked_reason: string | null;
  warnings: string[];
  pii_count: number;
  threats_count: number;
  text_modified: boolean;
}

interface TestSuiteSummary {
  total_tests: number;
  blocked: number;
  modified: number;
  with_warnings: number;
  passed_clean: number;
}

interface CustomTestItem {
  label: string;
  text: string;
  category: string;
  expected: string;
  check_type: string;
  confidence: number;
}

export function GuardrailsPanel() {
  const [status, setStatus] = useState<GuardrailsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [testData, setTestData] = useState<Record<string, TestDataItem[]>>({});
  const [testResults, setTestResults] = useState<TestResult[] | null>(null);
  const [testSummary, setTestSummary] = useState<TestSuiteSummary | null>(null);
  const [runningTests, setRunningTests] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [customTests, setCustomTests] = useState<CustomTestItem[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTest, setNewTest] = useState<CustomTestItem>({
    label: '',
    text: '',
    category: 'custom',
    expected: '',
    check_type: 'input',
    confidence: 0.8,
  });

  const fetchStatus = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetchApi(`${API_BASE}/settings/guardrails/status`, { headers });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch guardrails status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleToggle = async (key: string, value: boolean) => {
    setToggling(true);
    try {
      const headers = await getAuthHeaders();

      if (key === 'enable_guardrails') {
        await fetchApi(`${API_BASE}/settings/guardrails/toggle`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ enabled: value }),
        });
      } else {
        await fetchApi(`${API_BASE}/settings/${key}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ value }),
        });
      }

      await fetchStatus();
    } catch (err) {
      console.error(`Failed to toggle ${key}:`, err);
    } finally {
      setToggling(false);
    }
  };

  const loadTestData = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetchApi(`${API_BASE}/settings/guardrails/test-data`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTestData(data.test_data ?? {});
      }
    } catch (err) {
      console.error('Failed to load test data:', err);
    }
  };

  const handleShowTestPanel = async () => {
    if (!showTestPanel) {
      await loadTestData();
    }
    setShowTestPanel(!showTestPanel);
    setTestResults(null);
    setTestSummary(null);
  };

  const runTests = async () => {
    setRunningTests(true);
    setTestResults(null);
    setTestSummary(null);
    try {
      const headers = await getAuthHeaders();
      const body = customTests.length > 0 ? JSON.stringify(customTests) : JSON.stringify(null);
      const res = await fetchApi(`${API_BASE}/settings/guardrails/test-data/run`, {
        method: 'POST',
        headers,
        body,
      });
      if (res.ok) {
        const data = await res.json();
        setTestResults(data.results);
        setTestSummary(data.summary);
        // Expand all categories that have results
        const cats = new Set<string>(data.results.map((r: TestResult) => r.category));
        setExpandedCategories(cats);
      }
    } catch (err) {
      console.error('Failed to run tests:', err);
    } finally {
      setRunningTests(false);
    }
  };

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const addCustomTest = () => {
    if (!newTest.label.trim() || !newTest.text.trim()) return;
    setCustomTests(prev => [...prev, { ...newTest }]);
    setNewTest({ label: '', text: '', category: 'custom', expected: '', check_type: 'input', confidence: 0.8 });
    setShowAddForm(false);
  };

  const removeCustomTest = (index: number) => {
    setCustomTests(prev => prev.filter((_, i) => i !== index));
  };

  const getResultIcon = (result: TestResult) => {
    if (!result.actual_safe) return <XCircle size={16} className="text-red-500" />;
    if (result.text_modified || result.warnings.length > 0) return <AlertTriangle size={16} className="text-amber-500" />;
    return <CheckCircle size={16} className="text-green-500" />;
  };

  const getSafetyBadge = (level: string) => {
    const colors: Record<string, string> = {
      safe: 'bg-green-100 text-green-700',
      low_risk: 'bg-yellow-100 text-yellow-700',
      medium_risk: 'bg-orange-100 text-orange-700',
      high_risk: 'bg-red-100 text-red-700',
      blocked: 'bg-red-200 text-red-800',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${colors[level] || 'bg-gray-100 text-gray-700'}`}>
        {level.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Loading guardrails settings...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Master Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3">
          <Shield size={20} className={status?.enable_guardrails ? 'text-green-600' : 'text-gray-400'} />
          <div>
            <p className="font-medium text-gray-800">Guardrails Protection</p>
            <p className="text-sm text-gray-500">Enable input/output safety checks on all chat queries</p>
          </div>
        </div>
        <button
          onClick={() => handleToggle('enable_guardrails', !status?.enable_guardrails)}
          disabled={toggling}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            status?.enable_guardrails ? 'bg-green-500' : 'bg-gray-300'
          } ${toggling ? 'opacity-50' : ''}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              status?.enable_guardrails ? 'translate-x-6' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Component Toggles */}
      {status?.enable_guardrails && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">Components</h4>

          {[
            { key: 'guardrails_enable_pii_masking', label: 'PII Masking', desc: 'Mask emails, phones, SSNs, credit cards, API keys', value: status.components.pii_masking },
            { key: 'guardrails_enable_threat_detection', label: 'Threat Detection', desc: 'Block prompt injection, SQL injection, XSS, command injection', value: status.components.threat_detection },
            { key: 'guardrails_enable_topic_guard', label: 'Topic Guard', desc: 'Flag off-topic queries outside shipping/logistics', value: status.components.topic_guard },
            { key: 'guardrails_enable_output_guard', label: 'Output Safety', desc: 'Filter credentials and warn on dangerous actions in responses', value: status.components.output_guard },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50">
              <div>
                <p className="text-sm font-medium text-gray-700">{item.label}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
              <button
                onClick={() => handleToggle(item.key, !item.value)}
                disabled={toggling}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  item.value ? 'bg-green-500' : 'bg-gray-300'
                } ${toggling ? 'opacity-50' : ''}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    item.value ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          ))}

          {/* Strict Mode */}
          <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 border-t border-gray-100 mt-2 pt-3">
            <div>
              <p className="text-sm font-medium text-gray-700">Strict Mode</p>
              <p className="text-xs text-gray-500">Block on <strong>any</strong> risk level (not just high severity)</p>
            </div>
            <button
              onClick={() => handleToggle('guardrails_strict_mode', !status.strict_mode)}
              disabled={toggling}
              className={`relative w-10 h-5 rounded-full transition-colors ${
                status.strict_mode ? 'bg-red-500' : 'bg-gray-300'
              } ${toggling ? 'opacity-50' : ''}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  status.strict_mode ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      )}

      {/* Generate Test Data Button */}
      <div className="border-t border-gray-200 pt-4">
        <button
          onClick={handleShowTestPanel}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-200 w-full justify-center font-medium"
        >
          <Shield size={16} />
          {showTestPanel ? 'Hide Test Panel' : 'Generate Test Data for Guardrail Checks'}
        </button>
      </div>

      {/* Test Panel */}
      {showTestPanel && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Test Panel Header */}
          <div className="bg-gray-50 p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-800">Guardrail Test Suite</h4>
                <p className="text-sm text-gray-500 mt-1">
                  {Object.values(testData ?? {}).flat().length} default tests + {customTests.length} custom tests
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Plus size={14} />
                  Add Custom Test
                </button>
                <button
                  onClick={runTests}
                  disabled={runningTests}
                  className="flex items-center gap-1 px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {runningTests ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                  Run Tests
                </button>
              </div>
            </div>
          </div>

          {/* Add Custom Test Form */}
          {showAddForm && (
            <div className="p-4 bg-blue-50 border-b border-blue-100 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Label</label>
                  <input
                    type="text"
                    value={newTest.label}
                    onChange={e => setNewTest(prev => ({ ...prev, label: e.target.value }))}
                    placeholder="e.g., Custom PII test"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Expected Outcome</label>
                  <input
                    type="text"
                    value={newTest.expected}
                    onChange={e => setNewTest(prev => ({ ...prev, expected: e.target.value }))}
                    placeholder="e.g., Blocked, PII masked, Safe"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Test Text</label>
                <textarea
                  value={newTest.text}
                  onChange={e => setNewTest(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="Enter the text to test against guardrails..."
                  rows={2}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-700">Check Type:</label>
                  <select
                    value={newTest.check_type}
                    onChange={e => setNewTest(prev => ({ ...prev, check_type: e.target.value }))}
                    className="text-sm border border-gray-300 rounded-lg px-2 py-1"
                  >
                    <option value="input">Input (user query)</option>
                    <option value="output">Output (AI response)</option>
                  </select>
                </div>
                {newTest.check_type === 'output' && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-700">Confidence:</label>
                    <input
                      type="number"
                      value={newTest.confidence}
                      onChange={e => setNewTest(prev => ({ ...prev, confidence: parseFloat(e.target.value) || 0.8 }))}
                      min={0}
                      max={1}
                      step={0.1}
                      className="w-20 text-sm border border-gray-300 rounded-lg px-2 py-1"
                    />
                  </div>
                )}
                <div className="flex-1" />
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={addCustomTest}
                  disabled={!newTest.label.trim() || !newTest.text.trim()}
                  className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  Add Test
                </button>
              </div>
            </div>
          )}

          {/* Custom Tests List */}
          {customTests.length > 0 && (
            <div className="p-3 bg-amber-50 border-b border-amber-100">
              <p className="text-xs font-medium text-amber-700 mb-2">Custom Tests ({customTests.length})</p>
              <div className="space-y-1">
                {customTests.map((test, i) => (
                  <div key={i} className="flex items-center justify-between py-1 px-2 bg-white rounded text-sm">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <span className="font-medium text-gray-700 shrink-0">{test.label}</span>
                      <span className="text-gray-400 truncate">{test.text.substring(0, 60)}...</span>
                    </div>
                    <button onClick={() => removeCustomTest(i)} className="text-red-400 hover:text-red-600 shrink-0 ml-2">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Test Results Summary */}
          {testSummary && (
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-5 gap-3 text-center">
                <div className="bg-white rounded-lg p-2 border">
                  <p className="text-lg font-bold text-gray-800">{testSummary.total_tests}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div className="bg-white rounded-lg p-2 border">
                  <p className="text-lg font-bold text-red-600">{testSummary.blocked}</p>
                  <p className="text-xs text-gray-500">Blocked</p>
                </div>
                <div className="bg-white rounded-lg p-2 border">
                  <p className="text-lg font-bold text-amber-600">{testSummary.modified}</p>
                  <p className="text-xs text-gray-500">Modified</p>
                </div>
                <div className="bg-white rounded-lg p-2 border">
                  <p className="text-lg font-bold text-yellow-600">{testSummary.with_warnings}</p>
                  <p className="text-xs text-gray-500">Warnings</p>
                </div>
                <div className="bg-white rounded-lg p-2 border">
                  <p className="text-lg font-bold text-green-600">{testSummary.passed_clean}</p>
                  <p className="text-xs text-gray-500">Clean</p>
                </div>
              </div>
            </div>
          )}

          {/* Test Results by Category */}
          {testResults && (
            <div className="max-h-96 overflow-y-auto">
              {Array.from(new Set(testResults.map(r => r.category))).map(category => {
                const categoryResults = testResults.filter(r => r.category === category);
                const isExpanded = expandedCategories.has(category);

                return (
                  <div key={category} className="border-b border-gray-100 last:border-b-0">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span className="text-sm font-medium text-gray-700">
                          {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <span className="text-xs text-gray-400">({categoryResults.length} tests)</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {categoryResults.some(r => !r.actual_safe) && (
                          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                            {categoryResults.filter(r => !r.actual_safe).length} blocked
                          </span>
                        )}
                        {categoryResults.some(r => r.text_modified && r.actual_safe) && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                            {categoryResults.filter(r => r.text_modified && r.actual_safe).length} modified
                          </span>
                        )}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-3 space-y-2">
                        {categoryResults.map((result, idx) => (
                          <div key={idx} className="flex items-start gap-2 py-1.5 px-3 bg-gray-50 rounded-lg text-sm">
                            <div className="mt-0.5 shrink-0">{getResultIcon(result)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-gray-700">{result.label}</span>
                                {getSafetyBadge(result.actual_safety_level)}
                                {result.pii_count > 0 && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                    {result.pii_count} PII
                                  </span>
                                )}
                                {result.threats_count > 0 && (
                                  <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                    {result.threats_count} threats
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5 truncate">{result.text}</p>
                              {result.blocked_reason && (
                                <p className="text-xs text-red-600 mt-0.5">{result.blocked_reason}</p>
                              )}
                              {result.warnings.length > 0 && (
                                <p className="text-xs text-amber-600 mt-0.5">{result.warnings.join('; ')}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Default Test Data Preview (when no results yet) */}
          {!testResults && Object.keys(testData ?? {}).length > 0 && (
            <div className="max-h-64 overflow-y-auto">
              {Object.entries(testData).map(([category, tests]) => (
                <div key={category} className="border-b border-gray-100 last:border-b-0">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      {expandedCategories.has(category) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <span className="text-sm font-medium text-gray-700">
                        {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <span className="text-xs text-gray-400">({tests.length} tests)</span>
                    </div>
                  </button>
                  {expandedCategories.has(category) && (
                    <div className="px-4 pb-2 space-y-1">
                      {tests.map((test, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1 px-2 text-sm text-gray-600 bg-gray-50 rounded">
                          <span className="font-medium">{test.label}</span>
                          <span className="text-xs text-gray-400">{test.expected}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
